import type { ReactNode } from 'react';

import { c, msgid } from 'ttag';

import VpnLogo from '@proton/components/components/logo/VpnLogo';
import VpnPassLogo from '@proton/components/components/logo/VpnPassLogo';
import SkeletonLoader from '@proton/components/components/skeletonLoader/SkeletonLoader';
import { getCalendarAppFeature } from '@proton/components/containers/payments/features/calendar';
import { getDriveAppFeature } from '@proton/components/containers/payments/features/drive';
import type { PlanCardFeatureDefinition } from '@proton/components/containers/payments/features/interface';
import { getMailAppFeature } from '@proton/components/containers/payments/features/mail';
import {
    get2FAAuthenticator,
    getDarkWebMonitoring,
    getHideMyEmailAliases,
    getItems,
    getLoginsAndNotes,
    getPassAppFeature,
    getPasswordHealth,
} from '@proton/components/containers/payments/features/pass';
import {
    getAdvancedVPNCustomizations,
    getBandwidth,
    getCountries,
    getNetShield,
    getNetShieldText,
    getNoAds,
    getProtectDevices,
    getStreamingText,
    getVPNAppFeature,
    getVPNSpeed,
} from '@proton/components/containers/payments/features/vpn';
import { getWalletAppFeature } from '@proton/components/containers/payments/features/wallet';
import FreeLogo from '@proton/components/containers/payments/subscription/FreeLogo/FreeLogo';
import { IcBrandAndroid } from '@proton/icons/icons/IcBrandAndroid';
import { IcBrandApple } from '@proton/icons/icons/IcBrandApple';
import { IcBrandIos } from '@proton/icons/icons/IcBrandIos';
import { IcBrandLinux } from '@proton/icons/icons/IcBrandLinux';
import { IcBrandWindows } from '@proton/icons/icons/IcBrandWindows';
import type { IconSize } from '@proton/icons/types';
import { PLANS, PLAN_NAMES } from '@proton/payments/core/constants';
import type { Plan } from '@proton/payments/core/plan/interface';
import { getFreeTitle } from '@proton/payments/core/subscription/freePlans';
import { APPS, BRAND_NAME, VPN_CONNECTIONS } from '@proton/shared/lib/constants';
import { getFreeServers, getPlusServers } from '@proton/shared/lib/vpn/features';
import { CSS_BASE_UNIT_SIZE } from '@proton/styles/constants';
import isTruthy from '@proton/utils/isTruthy';
import { VPN_SERVERS } from '@proton/vpn/constants/vpnServers';

import bundleVpnPass from '../single-signup-v2/bundle-vpn-pass.svg';
import bundle from '../single-signup-v2/bundle.svg';
import VpnProLogo from './VpnProLogo';

export interface PlanInformation {
    logo: ReactNode;
    title: string;
    features: PlanCardFeatureDefinition[];
    bundle?: {
        title: string;
        features: PlanCardFeatureDefinition[];
    }[];
}

export const getPlanInformation = ({
    loading,
    selectedPlan,
}: {
    loading: boolean;
    selectedPlan: Plan;
}): PlanInformation | undefined => {
    const iconSize: IconSize = 7;
    const iconImgSize = iconSize * CSS_BASE_UNIT_SIZE;
    const countryPlaceholder: PlanCardFeatureDefinition | null = loading
        ? {
              id: 'countries',
              text: <SkeletonLoader width="6em" />,
              included: true,
          }
        : null;

    if (selectedPlan.Name === PLANS.FREE) {
        const freeServers = getFreeServers(VPN_SERVERS.free.servers, VPN_SERVERS.free.countries);
        return {
            logo: <FreeLogo size={iconImgSize} app={APPS.PROTONVPN_SETTINGS} />,
            title: getFreeTitle(BRAND_NAME),
            features: [countryPlaceholder || getCountries(freeServers), getNoAds(), getBandwidth()],
        };
    }

    if (selectedPlan.Name === PLANS.VPN2024) {
        const plusServers = getPlusServers(VPN_SERVERS.paid.servers, VPN_SERVERS.paid.countries);

        return {
            logo: <VpnLogo variant="glyph-only" size={iconSize} />,
            title: selectedPlan.Title,
            features: [
                countryPlaceholder || getCountries(plusServers),
                { id: 'streaming', text: getStreamingText(), included: true },
                getProtectDevices(VPN_CONNECTIONS),
                {
                    id: 'apps-for',
                    text: (
                        <div className="flex items-center">
                            {c('new_plans: feature').t`Apps for`}
                            <span className="ml-1 inline-flex gap-1.5">
                                <IcBrandWindows />
                                <IcBrandApple />
                                <IcBrandAndroid />
                                <IcBrandIos />
                                <IcBrandLinux />
                            </span>
                        </div>
                    ),
                    included: true,
                },
                { id: 'speed', text: c('new_plans: feature').t`Up to 10 Gbps`, included: true },
                { id: 'net-shield', text: getNetShieldText(), included: true },
            ].filter(isTruthy),
        };
    }

    if (selectedPlan.Name === PLANS.BUNDLE) {
        return {
            logo: (
                <div>
                    <img src={bundle} width={iconImgSize} height={iconImgSize} alt={selectedPlan.Title} />
                </div>
            ),
            title: selectedPlan.Title,
            features: [
                getMailAppFeature(),
                getCalendarAppFeature(),
                getDriveAppFeature(),
                getVPNAppFeature(),
                getPassAppFeature(),
                getWalletAppFeature(),
            ],
        };
    }

    if (selectedPlan.Name === PLANS.VPN_PASS_BUNDLE) {
        return {
            logo: (
                <div>
                    <img src={bundleVpnPass} width={iconImgSize} height={iconImgSize} alt={selectedPlan.Title} />
                </div>
            ),
            title: selectedPlan.Title,
            features: [],
            bundle: [
                {
                    title: PLAN_NAMES[PLANS.VPN2024],
                    features: [
                        getVPNSpeed('highest'),
                        getProtectDevices(VPN_CONNECTIONS),
                        getNetShield(true),
                        getAdvancedVPNCustomizations(true),
                    ],
                },
                {
                    title: PLAN_NAMES[PLANS.PASS],
                    features: [
                        getLoginsAndNotes(),
                        getHideMyEmailAliases('unlimited'),
                        get2FAAuthenticator(true),
                        getItems(),
                        getDarkWebMonitoring(),
                        getPasswordHealth(),
                    ],
                },
            ],
        };
    }

    const serversInNCountries = c('new_plans: feature').ngettext(
        msgid`Servers in ${VPN_SERVERS.paid.countries}+ country`,
        `Servers in ${VPN_SERVERS.paid.countries}+ countries`,
        VPN_SERVERS.paid.countries
    );

    if (selectedPlan.Name === PLANS.VPN_PRO) {
        return {
            logo: <VpnProLogo size={iconSize} />,
            title: selectedPlan.Title,
            features: [
                {
                    id: 'advanced-network-security',
                    text: c('new_plans: feature').t`Advanced network security`,
                    included: true,
                },
                countryPlaceholder || {
                    id: 'countries',
                    text: serversInNCountries,
                    included: true,
                },
                {
                    id: '24x7-support',
                    text: c('new_plans: feature').t`24/7 support`,
                    included: true,
                },
                {
                    id: 'centralized-settings-and-billing',
                    text: c('new_plans: feature').t`Centralized settings and billings`,
                    included: true,
                },
            ],
        };
    }

    if (selectedPlan.Name === PLANS.VPN_BUSINESS) {
        return {
            logo: <VpnLogo variant="glyph-only" size={iconSize} />,
            title: selectedPlan.Title,
            features: [
                {
                    id: 'advanced-network-security',
                    text: c('new_plans: feature').t`Advanced network security`,
                    included: true,
                },
                countryPlaceholder || {
                    id: 'countries',
                    text: serversInNCountries,
                    included: true,
                },
                {
                    id: 'dedicated-servers-and-ip',
                    text: c('new_plans: feature').t`Dedicated servers and IP`,
                    included: true,
                },
                {
                    id: '24x7-support',
                    text: c('new_plans: feature').t`24/7 support`,
                    included: true,
                },
                {
                    id: 'centralized-settings-and-billing',
                    text: c('new_plans: feature').t`Centralized settings and billings`,
                    included: true,
                },
            ],
        };
    }

    if (selectedPlan.Name === PLANS.VPN_PASS_BUNDLE_BUSINESS) {
        return {
            logo: <VpnPassLogo size={iconSize} />,
            title: selectedPlan.Title,
            features: [
                {
                    id: 'advanced-network-security',
                    text: c('new_plans: feature').t`Advanced network security`,
                    included: true,
                },
                {
                    id: 'dedicated-servers-and-ip',
                    text: c('new_plans: feature').t`Dedicated servers and IP`,
                    included: true,
                },
                {
                    id: 'secure-password-management',
                    text: c('new_plans: feature').t`Secure password management`,
                    included: true,
                },
                {
                    id: 'activity-logs-and-policy',
                    text: c('new_plans: feature').t`Activity logs and policy`,
                    included: true,
                },
                {
                    id: '24x7-support',
                    text: c('new_plans: feature').t`24/7 support`,
                    included: true,
                },
            ],
        };
    }

    if (selectedPlan.Name === PLANS.BUNDLE_PRO_2024) {
        return {
            logo: (
                <div>
                    <img src={bundle} width={iconImgSize} height={iconImgSize} alt={selectedPlan.Title} />
                </div>
            ),
            title: selectedPlan.Title,
            features: [
                {
                    id: 'advanced-workspace-security',
                    text: c('new_plans: feature').t`Advanced workspace security`,
                    included: true,
                },
                {
                    id: 'dedicated-servers-and-ip',
                    text: c('new_plans: feature').t`Dedicated servers and IP`,
                    included: true,
                },
                {
                    id: 'secure-password-management',
                    text: c('new_plans: feature').t`Secure password management`,
                    included: true,
                },
                {
                    id: 'activity-logs-and-policy',
                    text: c('new_plans: feature').t`Activity logs and policy`,
                    included: true,
                },
                {
                    id: '24x7-support',
                    text: c('new_plans: feature').t`24/7 support`,
                    included: true,
                },
            ],
        };
    }
};
